import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * H1_H2_Desc_4 Component
 * Displays a greeting, H1 heading, H2 heading, multiple description paragraphs,
 * a discount offer section with coupon code block (dashed border), CTA button,
 * and closing signature text
 * Centered layout with clean, simple design
 */
const H1H2Desc4 = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    greeting = "",
    title = "",
    subtitle = "",
    description = "",
    discountIntro = "",
    couponCode = "",
    discountWarning = "",
    buttonText = "",
    buttonLink = "#",
    closingText = "",
    signature = "",
    component_name = "H1_H2_Desc_4",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localGreeting, setLocalGreeting] = useState(greeting);
  const [localTitle, setLocalTitle] = useState(title);
  const [localSubtitle, setLocalSubtitle] = useState(subtitle);
  const [localDescription, setLocalDescription] = useState(description);
  const [localDiscountIntro, setLocalDiscountIntro] = useState(discountIntro);
  const [localCouponCode, setLocalCouponCode] = useState(couponCode);
  const [localDiscountWarning, setLocalDiscountWarning] = useState(discountWarning);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);
  const [localClosingText, setLocalClosingText] = useState(closingText);
  const [localSignature, setLocalSignature] = useState(signature);

  useEffect(() => {
    setLocalGreeting(greeting || "");
    setLocalTitle(title || "");
    setLocalSubtitle(subtitle || "");
    setLocalDescription(description || "");
    setLocalDiscountIntro(discountIntro || "");
    setLocalCouponCode(couponCode || "");
    setLocalDiscountWarning(discountWarning || "");
    setLocalButtonText(buttonText || "");
    setLocalButtonLink(buttonLink || "#");
    setLocalClosingText(closingText || "");
    setLocalSignature(signature || "");
  }, [id, greeting, title, subtitle, description, discountIntro, couponCode, discountWarning, buttonText, buttonLink, closingText, signature]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      greeting: localGreeting,
      title: localTitle,
      subtitle: localSubtitle,
      description: localDescription,
      discountIntro: localDiscountIntro,
      couponCode: localCouponCode,
      discountWarning: localDiscountWarning,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
      closingText: localClosingText,
      signature: localSignature,
    });
    setIsEditing(false);
  };

  // Helper function to split description into paragraphs
  const getParagraphs = (text) => {
    if (!text) return [];
    // Split by double newlines
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
    if (paragraphs.length === 0 && text.trim()) {
      return text.split(/\n/).filter((p) => p.trim());
    }
    return paragraphs;
  };

  // Helper function to split closing/signature into lines
  const getClosingLines = (text) => {
    if (!text) return [];
    return text.split(/\n/).filter((line) => line.trim());
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component h1-h2-desc-4-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit H1 & H2 Heading with Description & Coupon Code</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Greeting</label>
          <input
            className="form-control"
            value={localGreeting}
            onChange={(e) => setLocalGreeting(e.target.value)}
            placeholder="Enter greeting (e.g., Hey *|FNAME|*!)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Main Heading (H1)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter main heading"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Subheading (H2) - Optional</label>
          <input
            className="form-control"
            value={localSubtitle}
            onChange={(e) => setLocalSubtitle(e.target.value)}
            placeholder="Enter subheading (optional)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description. Use double line breaks to separate paragraphs."
            rows={6}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Discount Introduction Text</label>
          <input
            className="form-control"
            value={localDiscountIntro}
            onChange={(e) => setLocalDiscountIntro(e.target.value)}
            placeholder="Enter discount introduction text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Coupon Code</label>
          <input
            className="form-control"
            value={localCouponCode}
            onChange={(e) => setLocalCouponCode(e.target.value)}
            placeholder="Enter coupon code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Discount Warning Text</label>
          <input
            className="form-control"
            value={localDiscountWarning}
            onChange={(e) => setLocalDiscountWarning(e.target.value)}
            placeholder="Enter warning text about discount expiration"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Text</label>
          <input
            className="form-control"
            value={localButtonText}
            onChange={(e) => setLocalButtonText(e.target.value)}
            placeholder="Enter button text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Link</label>
          <input
            className="form-control"
            value={localButtonLink}
            onChange={(e) => setLocalButtonLink(e.target.value)}
            placeholder="Enter button link URL"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Closing Text</label>
          <textarea
            className="form-control"
            value={localClosingText}
            onChange={(e) => setLocalClosingText(e.target.value)}
            placeholder="Enter closing text. Use double line breaks to separate paragraphs."
            rows={3}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Signature</label>
          <textarea
            className="form-control"
            value={localSignature}
            onChange={(e) => setLocalSignature(e.target.value)}
            placeholder="Enter signature. Use line breaks to separate lines."
            rows={2}
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const descriptionParagraphs = getParagraphs(localDescription);
  const closingParagraphs = getParagraphs(localClosingText);
  const signatureLines = getClosingLines(localSignature);

  return (
    <>
      {!previewMode && (
        <div className="email-component h1-h2-desc-4 mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tbody>
          <tr>
            <td
              align="center"
              style={{
                padding: "32px 16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tbody>
                  {/* Greeting */}
                  {localGreeting && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "12px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "18px",
                            color: primaryColor || "#333333",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localGreeting}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Main Heading (H1) */}
                  {localTitle && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <h1
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            margin: "0 0 16px 0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localTitle}
                        </h1>
                      </td>
                    </tr>
                  )}

                  {/* Subheading (H2) - Optional */}
                  {localSubtitle && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <h2
                          style={{
                            fontSize: "22px",
                            fontWeight: "600",
                            color: primaryColor || "#333333",
                            margin: "0 0 16px 0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localSubtitle}
                        </h2>
                      </td>
                    </tr>
                  )}

                  {/* Description Paragraphs */}
                  {descriptionParagraphs.length > 0 &&
                    descriptionParagraphs.map((paragraph, index) => (
                      <tr key={index}>
                        <td
                          align="left"
                          style={{
                            paddingBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              color: primaryColor || "#333333",
                              lineHeight: "1.6",
                              margin: "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              textAlign: "left",
                            }}
                          >
                            {paragraph.trim()}
                          </p>
                        </td>
                      </tr>
                    ))}

                  {/* Discount Introduction */}
                  {localDiscountIntro && (
                    <tr>
                      <td
                        align="left"
                        style={{
                          paddingTop: "16px",
                          paddingBottom: "12px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "left",
                          }}
                        >
                          {localDiscountIntro}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Coupon Code Block with Dashed Border */}
                  {localCouponCode && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "16px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            border: `2px dashed ${secondaryColor || "#a1894c"}`,
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            padding: "20px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tbody>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: primaryColor || "#333333",
                                    letterSpacing: "1px",
                                    textTransform: "uppercase",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {localCouponCode}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Discount Warning */}
                  {localDiscountWarning && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingTop: "12px",
                          paddingBottom: "24px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localDiscountWarning}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* CTA Button */}
                  {localButtonText && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{ display: "inline-block" }}
                        >
                          <tr>
                            <td
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                border: `2px solid ${secondaryColor || "#a1894c"}`,
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href={localButtonLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  padding: "12px 24px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: secondaryColor || "#a1894c",
                                  textDecoration: "none",
                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                }}
                              >
                                {localButtonText}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Closing Text Paragraphs */}
                  {closingParagraphs.length > 0 &&
                    closingParagraphs.map((paragraph, index) => (
                      <tr key={index}>
                        <td
                          align="left"
                          style={{
                            paddingBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              color: primaryColor || "#333333",
                              lineHeight: "1.6",
                              margin: "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              textAlign: "left",
                            }}
                          >
                            {paragraph.trim()}
                          </p>
                        </td>
                      </tr>
                    ))}

                  {/* Signature */}
                  {signatureLines.length > 0 && (
                    <tr>
                      <td
                        align="left"
                        style={{
                          paddingTop: "8px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {signatureLines.map((line, index) => (
                          <p
                            key={index}
                            style={{
                              fontSize: "16px",
                              color: primaryColor || "#333333",
                              lineHeight: "1.6",
                              margin: index < signatureLines.length - 1 ? "0 0 4px 0" : "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              textAlign: "left",
                            }}
                          >
                            {line.trim()}
                          </p>
                        ))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default H1H2Desc4;

